import { documentClickListener } from '../../../utils/document-listener';
import {
  VI_TYPE,
  VC_TYPE,
  CUSTOM_AMOUNT,
  ERROR_VC_GROUP,
  BUNDLE_TYPE,
  GK_TYPE,
} from '../../../utils/store-helper';
import events from '../../events';
import { showNotification } from '../../helpers/common';
import { getStore } from '../../store';
import { translations } from '../../translations';
import * as loader from '../loader';
import { showMoreBundle } from './bundle-modal';
import { computeCatalogButtons } from './compute-catalog-buttons';
import {
  unmountSectionPreloader,
  renderSectionPreloader,
} from './section-preloader-render';
import { unmountStoreSection, renderEmptyTemplate } from './section-render';
import { StoreLoaderService } from './services/loader';

const switchTabs = (currentTab, block) => {
  const ACTIVE_TAB_CLASS = 'store__tab--active';
  const HIDDEN_SECTION_CLASS = 'store__content--hidden';

  if (currentTab.classList.contains(ACTIVE_TAB_CLASS)) {
    return;
  }

  const sectionId = currentTab.dataset.id;
  const allSections = block.querySelectorAll('.store__content');
  const currentSection = block.querySelector(
    `[data-store-section-id="${sectionId}"]`
  );

  block
    .querySelectorAll(`.${ACTIVE_TAB_CLASS}`)[0]
    .classList.remove(ACTIVE_TAB_CLASS);
  currentTab.classList.add(ACTIVE_TAB_CLASS);

  allSections.forEach((section) => {
    section.classList.add(HIDDEN_SECTION_CLASS);
  });
  if (currentSection !== null) {
    currentSection.classList.remove(HIDDEN_SECTION_CLASS);
  }
};

const fetchCatalogs = async ({
  universalItemsSections,
  storeAPIDataService,
  blockId,
}) => {
  const uniqueVirtualItemsGroups = [
    ...new Set(
      universalItemsSections
        .filter(({ type }) => type === VI_TYPE)
        .map(({ id }) => id)
    ),
  ];

  const loadBundles =
    universalItemsSections.find(({ type }) => type === BUNDLE_TYPE) &&
    storeAPIDataService.loadBundles();
  const loadGameKeys =
    universalItemsSections.find(({ type }) => type === GK_TYPE) &&
    storeAPIDataService.loadGameKeys();
  const loadVirtualItems = Promise.all(
    uniqueVirtualItemsGroups.map(async (groupId) => ({
      groupId,
      data: await storeAPIDataService.loadVirtualItems(groupId),
    }))
  );

  const hasVCPacksOnPage = universalItemsSections.find(({ type }) =>
    [VC_TYPE, CUSTOM_AMOUNT].includes(type)
  );
  const loadVirtualCurrencyPacks =
    hasVCPacksOnPage && storeAPIDataService.loadVirtualCurrencyPacks();
  const loadVirtualCurrencies =
    hasVCPacksOnPage && storeAPIDataService.loadVirtualCurrencies();

  const resources = await Promise.all([
    loadBundles,
    loadGameKeys,
    loadVirtualItems,
    loadVirtualCurrencyPacks,
    loadVirtualCurrencies,
  ]);
  // $FlowFixMe
  if (resources.some((resource) => resource?.errorMessage)) {
    showNotification(translations['client.common_error'], 'error');
  }
  const [
    bundles = [],
    gameKeys = [],
    virtualItems = [],
    virtualCurrencyPacks = [],
    virtualCurrencies = [],
  ] = resources;

  window.dispatchEvent(
    new CustomEvent(events.store.dataLoaded, {
      detail: { blockId },
    })
  );

  return {
    bundles,
    gameKeys,
    virtualItems,
    virtualCurrencyPacks,
    virtualCurrencies,
  };
};

export const handleFetchingAndRenderingSections = async ({
  block,
  storeAPIDataService,
  blockStructure,
  enableCart,
  userToken,
}) => {
  type UniversalItemSection = {
    id: string;
    storeSectionId: string;
    type: string;
    sectionIndex: number;
  };
  const universalItemsSections: UniversalItemSection[] = [];

  documentClickListener.addListener({
    selector: '.store__tab',
    handle: (element) => {
      switchTabs(element, block);
    },
  });

  const store = getStore();
  const { landingServices } = store.getState();
  const { userService } = landingServices;

  const service = new StoreLoaderService(userService);
  loader.attach(service);

  window.dispatchEvent(
    new CustomEvent(events.store.init, {
      detail: { blockId: block.id },
    })
  );

  const sections = block.querySelectorAll('.store__content');
  const sectionLists = block.querySelectorAll('.store__list');
  sections.forEach((section, index) => {
    const { group: groupId, type } = section.dataset;
    const currentSectionList = sectionLists[Number(index)];

    // Adding preloader before fetching and updating catalog
    renderSectionPreloader(section);
    unmountStoreSection({ currentSectionList });

    const isEmptySection =
      ![GK_TYPE, BUNDLE_TYPE].includes(type) &&
      (!groupId || groupId === ERROR_VC_GROUP);
    if (isEmptySection) {
      unmountSectionPreloader(section);
      renderEmptyTemplate(currentSectionList, {
        isEmptyList: true,
        emptyListText: translations['client.store.no_items'],
      });
      return;
    }
    universalItemsSections.push({
      id: groupId,
      storeSectionId: section?.dataset?.storeSectionId,
      type,
      sectionIndex: index,
    });
  });

  const {
    bundles,
    gameKeys,
    virtualItems,
    virtualCurrencyPacks,
    virtualCurrencies,
  } = await fetchCatalogs({
    universalItemsSections,
    storeAPIDataService,
    blockId: block.id,
  });

  const buttons = computeCatalogButtons({
    bundles,
    gameKeys,
    virtualItems,
    virtualCurrencyPacks,
    virtualCurrencies,
    block,
    universalItemsSections,
    blockStructure,
    enableCart,
    userToken,
  });

  showMoreBundle();
  loader.detach(service);

  window.dispatchEvent(
    new CustomEvent(events.store.renderComplete, {
      detail: { block },
    })
  );

  return buttons;
};
